import 'regenerator-runtime/runtime';
import { SagaIterator } from 'redux-saga';
import { call, cancel, put, select, take, takeEvery } from 'redux-saga/effects';
import {
  DoRequestResponse,
  GlobalAction,
  GlobalActions,
  GlobalPut,
  VorbelegungPayload,
} from '@ps20-frontend/shop-integration-redux';
import {
  App,
  mapVorbelegungdaten,
  TkvVorbelegungdaten,
  Vorbelegungdaten,
} from '@ps20-frontend/frontend-models';
import { SpartenType, TkvVorvertragsdaten, VertragsdatenRequestModel } from '@ps20/bff-models';
import { AxiosError } from 'axios';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { TkvActions } from '../tkvActions';
import { IAppState } from '../appState';

export enum VorbelegungTkvRequestEventsEnum {
  VORBELEGUNG_TKV_REQUEST_SUCCESSFULL = 'VORBELEGUNG_TKV_REQUEST_SUCCESSFULL',
  VORBELEGUNG_TKV_REQUEST_FAILED = 'VORBELEGUNG_TKV_REQUEST_FAILED',
}

export function* watchVorbelegungRequested(): SagaIterator {
  yield takeEvery(GlobalActions.VORBELEGUNG_REQUESTED, safeSaga(vorbelegungSaga, App.TKV));
  yield takeEvery(
    VorbelegungTkvRequestEventsEnum.VORBELEGUNG_TKV_REQUEST_SUCCESSFULL,
    safeSaga(successCallback, App.TKV),
  );
  yield takeEvery(
    VorbelegungTkvRequestEventsEnum.VORBELEGUNG_TKV_REQUEST_FAILED,
    safeSaga(errorCallback, App.TKV),
  );
}

const globalActions = new GlobalActions();

export function* vorbelegungSaga(action: GlobalAction<VorbelegungPayload>): SagaIterator {
  const state: IAppState = yield select();
  if (action.payload.appId !== App.TKV || action.payload.angebotsId !== state.angebotsId) {
    return;
  }
  if (action.payload?.vorhandeneVorbelegungdaten) {
    yield* tkvDatenVorbelegen(action.payload.vorhandeneVorbelegungdaten, action.payload.angebotsId);
  } else {
    const globalPut = GlobalPut.getInstance();
    const vertragsdatenRequestModel: VertragsdatenRequestModel = {
      spLaufnummer: action.payload.spLaufnummer,
      vertragsnummer: action.payload.vertragsnummer,
    };
    yield call(
      [globalPut, globalPut.put],
      globalActions.doRequest({
        appId: App.TKV,
        angebotsId: action.payload.angebotsId,
        body: vertragsdatenRequestModel,
        successEvent: VorbelegungTkvRequestEventsEnum.VORBELEGUNG_TKV_REQUEST_SUCCESSFULL,
        errorEvent: VorbelegungTkvRequestEventsEnum.VORBELEGUNG_TKV_REQUEST_FAILED,
        url: 'privatschutz/api/vertragsdaten',
      }),
    );
  }
}

export function* successCallback(successAction: DoRequestResponse<TkvVorvertragsdaten>): SagaIterator {
  const state: IAppState = yield select();

  if (state.angebotsId !== successAction.doRequestAction.payload.angebotsId) {
    return yield cancel();
  }

  const tkvVorbelegungdaten: TkvVorbelegungdaten = mapVorbelegungdaten(
    SpartenType.TIERKRANKEN,
    successAction.payload,
  ) as TkvVorbelegungdaten;
  yield* tkvDatenVorbelegen(tkvVorbelegungdaten, successAction.doRequestAction.payload.angebotsId);
}

const tkvActions = new TkvActions();

function* tkvDatenVorbelegen(vorbelegungdaten: Vorbelegungdaten, angebotsId: string): SagaIterator {
  const state: IAppState = yield select();
  let beibehalten = false;
  const tkvVorbelegungdaten: TkvVorbelegungdaten = vorbelegungdaten as TkvVorbelegungdaten;
  const globalPut = GlobalPut.getInstance();
  tkvVorbelegungdaten.angebotsId = state.angebotsId;

  if (state.produkt?.produktlinien.length === 0) {
    yield take(GlobalActions.PRODUKT_RECEIVED);
  }
  if (vorbelegungdaten.internerVertragsbezugs) {
    yield put(
      globalActions.internerVertragsbezugReceived(
        vorbelegungdaten.internerVertragsbezugs,
        App.TKV,
        state.angebotsId,
      ),
    );
  }

  if (!state.vertragsoptionen?.beibehalten) {
    yield put(tkvActions.vorbelegungForTkvRequested(tkvVorbelegungdaten));
    yield put(tkvActions.produktZusatzOptionenVorbelegt(tkvVorbelegungdaten));

    yield call(
      [globalPut, globalPut.put],
      globalActions.navigationRequested({
        appId: App.TKV,
        angebotsId,
        subroute: '/eingabe',
      }),
    );
  } else {
    beibehalten = true;
    yield call(
      [globalPut, globalPut.put],
      globalActions.vorbelegungForBeibehaltungRequested(tkvVorbelegungdaten),
    );
  }
  yield call(
    [globalPut, globalPut.put],
    globalActions.vorbelegungFinished({
      appId: App.TKV,
      vorhandeneVorbelegungdaten: vorbelegungdaten,
      angebotsId,
      beibehalten,
    }),
  );
}

export function* errorCallback(error: DoRequestResponse<AxiosError>): SagaIterator {
  const state: IAppState = yield select();
  const globalPut = GlobalPut.getInstance();
  if (state.angebotsId !== error.doRequestAction.payload.angebotsId) {
    return yield cancel();
  }
  yield call(
    globalPut.put,
    globalActions.vorbelegungFinished({
      appId: App.TKV,
      angebotsId: state.angebotsId,
    }),
  );
  yield cancel();
}
